import { calculateOrchestrationScore } from './scoring';
import type { AgentPersona, OrchestrationScore, TaskType, ToolConnector, WorkflowTemplate } from '../types/entities';

export const agentPersonas: AgentPersona[] = [
  {
    id: 'research-agent',
    name: 'Research Agent',
    description: 'Collects market, news, patent, and competitor evidence before synthesis.',
    role: 'Research',
    strengths: ['source discovery', 'summarization', 'evidence scoring'],
    weaknesses: ['requires source verification', 'can over-collect low-value sources'],
    recommendedTools: ['web-search-api', 'rss-reader', 'notion-api'],
    createdBy: 'system'
  },
  {
    id: 'coding-agent',
    name: 'Coding Agent',
    description: 'Implements scoped code changes with tests and implementation logs.',
    role: 'Engineering',
    strengths: ['implementation', 'refactoring', 'test generation'],
    weaknesses: ['needs explicit repo constraints', 'should not own secrets'],
    recommendedTools: ['github-mcp', 'firebase'],
    createdBy: 'system'
  },
  {
    id: 'qa-agent',
    name: 'QA Agent',
    description: 'Validates behavior, endpoints, UI flows, and deployment readiness.',
    role: 'Quality',
    strengths: ['acceptance checks', 'regression discovery', 'contract validation'],
    weaknesses: ['depends on available environments', 'may need seeded fixtures'],
    recommendedTools: ['github-mcp', 'web-search-api'],
    createdBy: 'system'
  },
  {
    id: 'product-manager-agent',
    name: 'Product Manager Agent',
    description: 'Turns business goals into task types, ranking criteria, and release notes.',
    role: 'Product',
    strengths: ['requirements analysis', 'prioritization', 'stakeholder summaries'],
    weaknesses: ['needs domain owner review', 'should not approve legal text'],
    recommendedTools: ['notion-api', 'slack-api'],
    createdBy: 'system'
  },
  {
    id: 'security-reviewer-agent',
    name: 'Security Reviewer Agent',
    description: 'Reviews auth, secret handling, data access, and connector risk.',
    role: 'Security',
    strengths: ['threat modeling', 'risk ranking', 'policy checks'],
    weaknesses: ['requires current deployment context', 'needs human risk acceptance'],
    recommendedTools: ['github-mcp', 'firebase'],
    createdBy: 'system'
  }
];

export const toolConnectors: ToolConnector[] = [
  { id: 'web-search-api', name: 'Web Search API', type: 'api', authType: 'apiKey', endpoint: '/connectors/web-search', description: 'Searches current web sources for evidence.', riskLevel: 'medium', setupDifficulty: 'moderate', evidenceRequired: true },
  { id: 'rss-reader', name: 'RSS Reader', type: 'rss', authType: 'none', endpoint: '/connectors/rss', description: 'Collects monitored feed entries for trend and news workflows.', riskLevel: 'low', setupDifficulty: 'easy', evidenceRequired: true },
  { id: 'github-mcp', name: 'GitHub MCP', type: 'mcp', authType: 'oauth', endpoint: '/mcp/github', description: 'Connects repositories, issues, pull requests, and CI context.', riskLevel: 'medium', setupDifficulty: 'moderate', evidenceRequired: true },
  { id: 'gmail-mcp', name: 'Gmail MCP', type: 'mcp', authType: 'oauth', endpoint: '/mcp/gmail', description: 'Reads mailbox context for support and briefing workflows.', riskLevel: 'high', setupDifficulty: 'hard', evidenceRequired: true },
  { id: 'google-calendar-mcp', name: 'Google Calendar MCP', type: 'mcp', authType: 'oauth', endpoint: '/mcp/google-calendar', description: 'Reads calendar context for scheduling workflows.', riskLevel: 'medium', setupDifficulty: 'moderate', evidenceRequired: true },
  { id: 'firebase', name: 'Firebase', type: 'database', authType: 'serviceAccount', endpoint: 'firestore.googleapis.com', description: 'Primary datastore for users, scores, workflows, and evidence.', riskLevel: 'high', setupDifficulty: 'moderate', evidenceRequired: true },
  { id: 'notion-api', name: 'Notion API', type: 'api', authType: 'oauth', endpoint: 'api.notion.com', description: 'Publishes and retrieves structured workspace notes.', riskLevel: 'medium', setupDifficulty: 'moderate', evidenceRequired: true },
  { id: 'slack-api', name: 'Slack API', type: 'api', authType: 'oauth', endpoint: 'slack.com/api', description: 'Sends approvals, workflow notices, and agent handoffs.', riskLevel: 'medium', setupDifficulty: 'moderate', evidenceRequired: true }
];

export const taskTypes: TaskType[] = [
  { id: 'market-research', name: 'Market research', category: 'Research', description: 'Collect and rank market signals.', requiredCapabilities: ['source discovery', 'synthesis'], recommendedAgents: ['research-agent', 'product-manager-agent'], recommendedTools: ['web-search-api', 'rss-reader', 'notion-api'] },
  { id: 'code-generation', name: 'Code generation', category: 'Engineering', description: 'Produce scoped implementation from requirements.', requiredCapabilities: ['code editing', 'testing'], recommendedAgents: ['coding-agent', 'qa-agent'], recommendedTools: ['github-mcp', 'firebase'] },
  { id: 'bug-fixing', name: 'Bug fixing', category: 'Engineering', description: 'Reproduce, patch, and verify defects.', requiredCapabilities: ['debugging', 'regression testing'], recommendedAgents: ['coding-agent', 'qa-agent'], recommendedTools: ['github-mcp'] },
  { id: 'customer-support', name: 'Customer support', category: 'Operations', description: 'Summarize and respond to customer issues.', requiredCapabilities: ['triage', 'response drafting'], recommendedAgents: ['research-agent', 'product-manager-agent'], recommendedTools: ['gmail-mcp', 'slack-api'] },
  { id: 'patent-tracking', name: 'Patent tracking', category: 'Research', description: 'Monitor patent updates and extract implications.', requiredCapabilities: ['monitoring', 'legal source review'], recommendedAgents: ['research-agent', 'security-reviewer-agent'], recommendedTools: ['web-search-api', 'rss-reader'] },
  { id: 'news-monitoring', name: 'News monitoring', category: 'Research', description: 'Track live feeds and summarize deltas.', requiredCapabilities: ['feed ingestion', 'summarization'], recommendedAgents: ['research-agent'], recommendedTools: ['rss-reader', 'web-search-api'] },
  { id: 'seo-article-generation', name: 'SEO article generation', category: 'Content', description: 'Create evidence-backed SEO article outlines and drafts.', requiredCapabilities: ['keyword research', 'content planning'], recommendedAgents: ['research-agent', 'product-manager-agent'], recommendedTools: ['web-search-api', 'notion-api'] },
  { id: 'saas-requirement-analysis', name: 'SaaS requirement analysis', category: 'Product', description: 'Convert SaaS concept notes into prioritized requirements.', requiredCapabilities: ['requirements analysis', 'workflow mapping'], recommendedAgents: ['product-manager-agent', 'security-reviewer-agent'], recommendedTools: ['notion-api', 'slack-api'] }
];

export const workflowTemplates: WorkflowTemplate[] = [
  {
    id: 'evidence-first-ranking',
    name: 'Evidence-first ranking',
    description: 'Collect sources, score connector fit, rank agent-tool combinations, and export a reviewable workflow.',
    steps: [
      { id: 'scope', name: 'Scope task', owner: 'human', description: 'Define task type, business goal, data limits, and acceptance criteria.', evidenceOutput: 'Decision record' },
      { id: 'collect', name: 'Collect evidence', owner: 'agent', description: 'Use approved sources and connectors to gather relevant evidence.', evidenceOutput: 'EvidenceLog entries' },
      { id: 'rank', name: 'Rank orchestration', owner: 'system', description: 'Apply weighted scoring and risk filters.', evidenceOutput: 'OrchestrationScore' },
      { id: 'review', name: 'Human review', owner: 'human', description: 'Approve high-risk connectors, cost, and privacy assumptions.', evidenceOutput: 'Decision record' }
    ],
    agentRoles: ['Research', 'Product', 'Security'],
    requiredTools: ['web-search-api', 'rss-reader', 'firebase'],
    mcpCompatible: true,
    exportFormat: 'json'
  }
];

export const orchestrationScores: OrchestrationScore[] = taskTypes.slice(0, 5).map((task, index) => {
  const reliability = 72 + index * 4;
  const taskFit = 76 + index * 3;
  const toolCompatibility = 70 + index * 5;
  const costEfficiency = 86 - index * 4;
  const evidenceQuality = 68 + index * 5;

  return {
    id: `${task.id}-score`,
    taskTypeId: task.id,
    agentCombination: task.recommendedAgents,
    toolCombination: task.recommendedTools,
    score: calculateOrchestrationScore({ reliability, taskFit, toolCompatibility, costEfficiency, evidenceQuality }),
    difficulty: index > 2 ? 'moderate' : 'easy',
    costLevel: index > 3 ? 'medium' : 'low',
    reliability,
    taskFit,
    toolCompatibility,
    costEfficiency,
    evidenceQuality,
    recommendedWorkflow: 'evidence-first-ranking',
    createdAt: new Date('2026-06-28T00:00:00.000Z').toISOString()
  };
});
